import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Select,
  Stack,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBolt,
  IconClick,
  IconLayoutGrid,
  IconPhoto,
  IconPlus,
  IconShare,
  IconShoppingBag,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import type { PreviewPayload } from '../inbox/template-preview';
import {
  CarouselBuilder,
  QuickReplyBuilder,
  type IgGenericCard,
  type QuickReply,
} from '../../routes/vendor/instagram.inbox';

/**
 * Instagram template builders for the WhatsApp-style `new`/`edit` routes. The
 * quick-reply + carousel builders are shared with the inbox composer (imported
 * from `instagram.inbox`); the remaining four live here. Each builder owns its
 * fields + validation + submit button and streams a live `onChange(preview)`;
 * pass `initial` to prefill for editing.
 */

export { CarouselBuilder, QuickReplyBuilder };
export type { IgGenericCard, QuickReply };

export type TemplateKind =
  | 'quick_replies'
  | 'generic'
  | 'button'
  | 'media'
  | 'media_share'
  | 'product';

export const KIND_META: Record<
  TemplateKind,
  { label: string; color: string; Icon: typeof IconBolt; hint: string }
> = {
  quick_replies: {
    label: 'Quick replies',
    color: 'pink',
    Icon: IconBolt,
    hint: 'A message with up to 13 tappable reply chips.',
  },
  generic: {
    label: 'Carousel',
    color: 'grape',
    Icon: IconLayoutGrid,
    hint: 'Up to 10 swipeable cards, each with an image, text and buttons.',
  },
  button: {
    label: 'Buttons',
    color: 'blue',
    Icon: IconClick,
    hint: 'A text message with up to 3 buttons (link / postback).',
  },
  media: {
    label: 'Media',
    color: 'teal',
    Icon: IconPhoto,
    hint: 'An image or video by URL with an optional caption.',
  },
  media_share: {
    label: 'Shared post',
    color: 'indigo',
    Icon: IconShare,
    hint: "Share one of the business's own published posts/reels by media id.",
  },
  product: {
    label: 'Products',
    color: 'orange',
    Icon: IconShoppingBag,
    hint: 'Share IG Shopping catalog products (requires a connected catalog).',
  },
};

/** Strict button union the IG Send API + zod DTO expect. */
export type ApiButton =
  | { type: 'web_url'; title: string; url: string }
  | { type: 'postback'; title: string; payload: string };

interface IgEditorBtn {
  type: 'web_url' | 'postback';
  title: string;
  url: string;
  payload: string;
}

/** Text + up to 3 buttons (web link / postback). */
export function ButtonTemplateBuilder({
  onSave,
  onChange,
  sending,
  submitLabel = 'Save template',
  initial,
}: {
  onSave: (p: { text: string; buttons: ApiButton[] }) => void;
  onChange?: (p: PreviewPayload) => void;
  sending: boolean;
  submitLabel?: string;
  initial?: { text: string; buttons: ApiButton[] };
}) {
  const [text, setText] = useState(initial?.text ?? '');
  const [buttons, setButtons] = useState<IgEditorBtn[]>(
    initial?.buttons?.length
      ? initial.buttons.map((b) => ({
          type: b.type,
          title: b.title,
          url: b.type === 'web_url' ? b.url : '',
          payload: b.type === 'postback' ? b.payload : '',
        }))
      : [{ type: 'web_url', title: '', url: '', payload: '' }],
  );
  useEffect(() => {
    onChange?.({
      text,
      buttons: buttons.map((b) => ({ type: b.type, title: b.title, url: b.url })),
    });
  }, [text, buttons, onChange]);

  const valid =
    text.trim().length > 0 &&
    buttons.length > 0 &&
    buttons.every(
      (b) => b.title.trim() && (b.type === 'web_url' ? b.url.trim() : b.payload.trim()),
    );

  function set(i: number, patch: Partial<IgEditorBtn>) {
    setButtons((bs) => bs.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  }

  return (
    <Stack gap="sm">
      <Textarea
        label="Message"
        placeholder="How can we help?"
        autosize
        minRows={2}
        maxRows={5}
        maxLength={640}
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
      />
      <Text size="sm" fw={600}>
        Buttons ({buttons.length}/3)
      </Text>
      {buttons.map((b, i) => (
        <Group key={i} wrap="nowrap" gap="xs" align="flex-end">
          <Select
            w={120}
            data={[
              { value: 'web_url', label: 'Link' },
              { value: 'postback', label: 'Postback' },
            ]}
            value={b.type}
            allowDeselect={false}
            onChange={(v) => set(i, { type: (v as 'web_url' | 'postback') ?? 'web_url' })}
          />
          <TextInput
            flex={1}
            placeholder="Button text"
            maxLength={20}
            value={b.title}
            onChange={(e) => set(i, { title: e.currentTarget.value })}
          />
          <TextInput
            flex={1}
            placeholder={b.type === 'web_url' ? 'https://…' : 'PAYLOAD'}
            value={b.type === 'web_url' ? b.url : b.payload}
            onChange={(e) =>
              set(
                i,
                b.type === 'web_url'
                  ? { url: e.currentTarget.value }
                  : { payload: e.currentTarget.value },
              )
            }
          />
          <ActionIcon
            variant="subtle"
            color="red"
            disabled={buttons.length === 1}
            onClick={() => setButtons((bs) => bs.filter((_, idx) => idx !== i))}
            aria-label="Remove button"
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
      <Group justify="space-between">
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          disabled={buttons.length >= 3}
          onClick={() =>
            setButtons((bs) => [...bs, { type: 'web_url', title: '', url: '', payload: '' }])
          }
        >
          Add button
        </Button>
        <Button
          color="pink"
          loading={sending}
          disabled={!valid}
          onClick={() =>
            onSave({
              text: text.trim(),
              buttons: buttons.map((b) =>
                b.type === 'web_url'
                  ? { type: 'web_url', title: b.title.trim(), url: b.url.trim() }
                  : { type: 'postback', title: b.title.trim(), payload: b.payload.trim() },
              ),
            })
          }
        >
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

/** Image/video by URL + optional caption. */
export function MediaTemplateBuilder({
  onSave,
  onChange,
  sending,
  submitLabel = 'Save template',
  initial,
}: {
  onSave: (p: { mediaType: 'image' | 'video'; url: string; caption?: string }) => void;
  onChange?: (p: PreviewPayload) => void;
  sending: boolean;
  submitLabel?: string;
  initial?: { mediaType: 'image' | 'video'; url: string; caption?: string };
}) {
  const [mediaType, setMediaType] = useState<'image' | 'video'>(initial?.mediaType ?? 'image');
  const [url, setUrl] = useState(initial?.url ?? '');
  const [caption, setCaption] = useState(initial?.caption ?? '');
  useEffect(() => {
    onChange?.({ mediaType, url, caption });
  }, [mediaType, url, caption, onChange]);
  const valid = /^https?:\/\//.test(url.trim());

  return (
    <Stack gap="sm">
      <Select
        label="Media type"
        data={[
          { value: 'image', label: 'Image' },
          { value: 'video', label: 'Video' },
        ]}
        value={mediaType}
        allowDeselect={false}
        onChange={(v) => setMediaType((v as 'image' | 'video') ?? 'image')}
      />
      <TextInput
        label="Media URL"
        description="Must be a public URL Instagram can fetch."
        placeholder="https://…/promo.jpg"
        value={url}
        onChange={(e) => setUrl(e.currentTarget.value)}
      />
      <Textarea
        label="Caption (optional)"
        placeholder="Sent as a follow-up message"
        autosize
        minRows={2}
        value={caption}
        onChange={(e) => setCaption(e.currentTarget.value)}
      />
      <Group justify="flex-end">
        <Button
          color="pink"
          loading={sending}
          disabled={!valid}
          onClick={() =>
            onSave({
              mediaType,
              url: url.trim(),
              ...(caption.trim() ? { caption: caption.trim() } : {}),
            })
          }
        >
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

/** Share one of the business's own published posts/reels by media id. */
export function MediaShareBuilder({
  onSave,
  onChange,
  sending,
  submitLabel = 'Save template',
  initial,
}: {
  onSave: (p: { mediaId: string }) => void;
  onChange?: (p: PreviewPayload) => void;
  sending: boolean;
  submitLabel?: string;
  initial?: { mediaId: string };
}) {
  const [mediaId, setMediaId] = useState(initial?.mediaId ?? '');
  useEffect(() => {
    onChange?.({ mediaId });
  }, [mediaId, onChange]);
  return (
    <Stack gap="sm">
      <TextInput
        label="Post / reel media id"
        description="The numeric media id of a post you own."
        placeholder="17900000000000000"
        value={mediaId}
        onChange={(e) => setMediaId(e.currentTarget.value)}
      />
      <Group justify="flex-end">
        <Button
          color="pink"
          loading={sending}
          disabled={!mediaId.trim()}
          onClick={() => onSave({ mediaId: mediaId.trim() })}
        >
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

/** Share IG Shopping catalog products — gated on a connected catalog. */
export function ProductBuilder({
  onSave,
  onChange,
  sending,
  submitLabel = 'Save template',
  initial,
}: {
  onSave: (p: { productIds: string[] }) => void;
  onChange?: (p: PreviewPayload) => void;
  sending: boolean;
  submitLabel?: string;
  initial?: { productIds: string[] };
}) {
  const [productIds, setProductIds] = useState<string[]>(initial?.productIds ?? []);
  useEffect(() => {
    onChange?.({ productIds });
  }, [productIds, onChange]);
  return (
    <Stack gap="sm">
      <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light">
        Product templates require a connected <b>Instagram Shopping catalog</b> and commerce
        permissions. Sends will fail until the catalog is set up and approved.
      </Alert>
      <TagsInput
        label="Catalog product / retailer ids"
        description="Up to 10 ids from your connected catalog."
        placeholder="Type an id and press Enter"
        value={productIds}
        onChange={setProductIds}
        maxTags={10}
      />
      <Group justify="flex-end">
        <Button
          color="pink"
          loading={sending}
          disabled={productIds.length === 0}
          onClick={() => onSave({ productIds })}
        >
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

/** Dispatch to the right builder by kind. `initial` is the stored API payload
 *  (edit) or undefined (new); `onSave` receives the kind-specific API payload. */
export function IgBuilderSwitch({
  kind,
  sending,
  submitLabel,
  initial,
  onChange,
  onSave,
}: {
  kind: TemplateKind;
  sending: boolean;
  submitLabel: string;
  initial?: unknown;
  onChange: (p: PreviewPayload) => void;
  onSave: (payload: unknown) => void;
}) {
  switch (kind) {
    case 'quick_replies':
      return (
        <QuickReplyBuilder
          sending={sending}
          submitLabel={submitLabel}
          onChange={onChange}
          onSend={(p) => onSave(p)}
          initial={initial as { text: string; quickReplies: QuickReply[] } | undefined}
        />
      );
    case 'generic':
      return (
        <CarouselBuilder
          sending={sending}
          submitLabel={submitLabel}
          onChange={onChange}
          onSend={(cards) => onSave({ elements: cards })}
          initial={(initial as { elements: IgGenericCard[] } | undefined)?.elements}
        />
      );
    case 'button':
      return (
        <ButtonTemplateBuilder
          sending={sending}
          submitLabel={submitLabel}
          onChange={onChange}
          onSave={onSave}
          initial={initial as { text: string; buttons: ApiButton[] } | undefined}
        />
      );
    case 'media':
      return (
        <MediaTemplateBuilder
          sending={sending}
          submitLabel={submitLabel}
          onChange={onChange}
          onSave={onSave}
          initial={
            initial as { mediaType: 'image' | 'video'; url: string; caption?: string } | undefined
          }
        />
      );
    case 'media_share':
      return (
        <MediaShareBuilder
          sending={sending}
          submitLabel={submitLabel}
          onChange={onChange}
          onSave={onSave}
          initial={initial as { mediaId: string } | undefined}
        />
      );
    case 'product':
      return (
        <ProductBuilder
          sending={sending}
          submitLabel={submitLabel}
          onChange={onChange}
          onSave={onSave}
          initial={initial as { productIds: string[] } | undefined}
        />
      );
  }
}
